/**
 * TopNavModel
 *
 * Navigation model and icon set for the customer-portal top nav. Relocated and
 * extended from the retired Layout sidebar (the old allNavigation and NavItem).
 *
 * Role tokens only: every icon below inherits currentColor and sets no colour.
 *
 * Authority: WIREFRAME-DASHBOARD-v0.17, ENGINE-BRIEF-TOPNAV-SIDEBAR-RETIRE-v1.1,
 * CHAIR-RULING-TOPNAV-CREDITNOTES-PRECUT-v1.0.
 *
 * Parity is router-derived (App.tsx), not assumed:
 *  - "Bank" is the v0.17 label for /transactions/import, which resolves to
 *    BankImportPage. Same route as the legacy "Import", new label.
 *  - Credit notes is omitted in phase 1: no /credit-notes route exists and the
 *    capability is invoice-scoped, so it slots back into More > Sales when a
 *    standalone page ships (ruling). No item, no badge.
 *  - Bills is a held slot: no route, marked coming, announced "Bills, coming soon".
 */

import type { ReactNode } from 'react';

export type Plan = 'sole_trader' | 'limited_company' | 'trial';

export type PrimaryItem = {
  label: string;
  to: string;
  end?: boolean;
  requiresPlan?: Plan;
};

/**
 * Primary bar, in v0.17 order. Text labels, no icons (the wireframe primary bar
 * is text only).
 *
 * Transactions carries end:true so it lights only on its own list and never on
 * /transactions/import, which Bank owns. Without it both would light on the
 * import route. The cost is that Transactions does not back-light on its create
 * and detail sub-routes; that is the lesser of the two, and is flagged for the
 * witness.
 */
export const PRIMARY_NAV: PrimaryItem[] = [
  { label: 'Bank', to: '/transactions/import' },
  { label: 'Invoices', to: '/invoices' },
  { label: 'Quotes', to: '/quotes' },
  { label: 'Contacts', to: '/contacts' },
  { label: 'Transactions', to: '/transactions', end: true },
  { label: 'Directors', to: '/directors', requiresPlan: 'limited_company' },
];

export type IconName =
  | 'bills' | 'reports' | 'accounts' | 'rules' | 'opening' | 'logs'
  | 'settings' | 'bug' | 'admin' | 'signout';

export type MoreItem = {
  label: string;
  to?: string;      // absent for a held, coming slot
  hint?: string;    // mono hint shown right-aligned, e.g. "chart of accounts"
  coming?: boolean; // held slot, announced, not navigable
  icon: IconName;
};

export type MoreGroup = { id: string; label: string; items: MoreItem[] };

/** More menu, in v0.17 order. Credit notes omitted (ruling); Bills held as coming. */
export const MORE_GROUPS: MoreGroup[] = [
  {
    id: 'sales',
    label: 'Sales',
    items: [
      { label: 'Bills', coming: true, icon: 'bills' },
    ],
  },
  {
    id: 'accounting',
    label: 'Accounting',
    items: [
      { label: 'Reports', to: '/reports', icon: 'reports' },
      { label: 'Accounts', to: '/accounts', hint: 'chart of accounts', icon: 'accounts' },
      { label: 'Rules', to: '/rules', icon: 'rules' },
    ],
  },
  {
    id: 'setup',
    label: 'Setup',
    items: [
      { label: 'Opening balances', to: '/opening-balances', icon: 'opening' },
    ],
  },
  {
    id: 'admin',
    label: 'Admin',
    items: [
      { label: 'Logs', to: '/logs', icon: 'logs' },
    ],
  },
];

/** Routes living inside More, used to light the More trigger when one is current. */
export const MORE_ROUTES: string[] = MORE_GROUPS
  .flatMap((g) => g.items)
  .map((i) => i.to)
  .filter((to): to is string => Boolean(to));

/** Active-path test. end matches the exact path only; otherwise path or any descendant. */
export function isPathActive(pathname: string, to: string, end?: boolean): boolean {
  if (end) return pathname === to;
  return pathname === to || pathname.startsWith(to + '/');
}

export function isMoreActive(pathname: string): boolean {
  return MORE_ROUTES.some((to) => isPathActive(pathname, to));
}

/** Present-org mark, first letters of the first two words. "Spey Systems Ltd" gives "SS". */
export function orgInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

// ---------------------------------------------------------------------------
// Icons. Each renders an svg using currentColor; size is set by the wrapper.
// ---------------------------------------------------------------------------

function glyph(path: ReactNode, strokeWidth = 1.7) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      aria-hidden="true"
      className="w-full h-full"
    >
      {path}
    </svg>
  );
}

export const Icons: Record<IconName, () => JSX.Element> = {
  bills: () => glyph(<path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 12h6m-6 4h4" />),
  reports: () => glyph(<path strokeLinecap="round" strokeLinejoin="round" d="M5 19V5h4v14H5zm6 0V9h4v10h-4zm6 0v-7h2v7h-2z" />),
  accounts: () => glyph(<path strokeLinecap="round" strokeLinejoin="round" d="M4 7h16M4 12h16M4 17h10" />),
  rules: () => glyph(<path strokeLinecap="round" strokeLinejoin="round" d="M3 4h18l-7 8v6l-4 2v-8z" />),
  opening: () => glyph(<path strokeLinecap="round" strokeLinejoin="round" d="M4 7h16M7 4v3m10-3v3M5 11h14v8H5zM9 15h2" />),
  logs: () => glyph(<path strokeLinecap="round" strokeLinejoin="round" d="M4 5h16v14H4zM8 9l3 3-3 3m5 0h4" />),
  settings: () => glyph(
    <>
      <circle cx="12" cy="12" r="3" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.4 15a1.6 1.6 0 00.32 1.77l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.6 1.6 0 00-2.7.65 1.6 1.6 0 01-3.06 0 1.6 1.6 0 00-2.7-.65l-.06.06a2 2 0 11-2.83-2.83l.06-.06A1.6 1.6 0 004.6 15a1.6 1.6 0 00-1.45-1H3a2 2 0 110-4h.09A1.6 1.6 0 004.6 9a1.6 1.6 0 00-.32-1.77l-.06-.06a2 2 0 112.83-2.83l.06.06A1.6 1.6 0 009 4.6a1.6 1.6 0 001-1.45V3a2 2 0 114 0v.09a1.6 1.6 0 001 1.45 1.6 1.6 0 001.77-.32l.06-.06a2 2 0 112.83 2.83l-.06.06A1.6 1.6 0 0019.4 9a1.6 1.6 0 001.45 1H21a2 2 0 110 4h-.09a1.6 1.6 0 00-1.45 1z" />
    </>
  ),
  bug: () => glyph(<path strokeLinecap="round" strokeLinejoin="round" d="M9 18l-1 2m8-2l1 2M5 11a7 7 0 0114 0v3a3 3 0 01-3 3H8a3 3 0 01-3-3z" />),
  admin: () => glyph(<path strokeLinecap="round" strokeLinejoin="round" d="M12 2l7 3v6c0 4.4-3 8-7 9-4-1-7-4.6-7-9V5z" />),
  signout: () => glyph(<path strokeLinecap="round" strokeLinejoin="round" d="M15 12H3m0 0l4-4m-4 4l4 4M11 5V4a2 2 0 012-2h5a2 2 0 012 2v16a2 2 0 01-2 2h-5a2 2 0 01-2-2v-1" />),
};

export const ChevronDown = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} aria-hidden="true" className="w-3 h-3 opacity-70 shrink-0">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" />
  </svg>
);

export const HamburgerIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true" className="w-[18px] h-[18px]">
    <path strokeLinecap="round" d="M4 7h16M4 12h16M4 17h16" />
  </svg>
);

export const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true" className="w-5 h-5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

export const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true" className="w-4 h-4">
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);
